import { FetchApi, IdentityApi } from '@backstage/core-plugin-api';
import { ListNotificationsResult, Notification } from './types';

// Talks directly to notification-service — a separate backend from every
// other plugin's api.ts, same "attach the token ourselves" rationale as
// runtimeExtension/api.ts: fetchApiRef only auto-authenticates requests to
// backend.baseUrl, and this service is a different origin.
async function fetchNotificationApi<T>(
  fetchApi: FetchApi,
  identityApi: IdentityApi,
  baseUrl: string,
  path: string,
  options?: RequestInit,
): Promise<T> {
  const { token } = await identityApi.getCredentials();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  let response: Response;
  try {
    response = await fetchApi.fetch(`${baseUrl}${path}`, {
      headers,
      ...options,
    });
  } catch {
    throw new Error(
      'Could not reach the notification service — it may be temporarily unavailable.',
    );
  }
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw Object.assign(new Error(body.error || response.statusText), body);
  }
  return response.json();
}

export async function listNotifications(
  fetchApi: FetchApi,
  identityApi: IdentityApi,
  baseUrl: string,
  page: number,
  limit: number,
): Promise<ListNotificationsResult> {
  return fetchNotificationApi<ListNotificationsResult>(
    fetchApi,
    identityApi,
    baseUrl,
    `/api/v1/notifications?page=${page}&limit=${limit}`,
  );
}

export async function getUnreadCount(
  fetchApi: FetchApi,
  identityApi: IdentityApi,
  baseUrl: string,
): Promise<number> {
  const data = await fetchNotificationApi<{ count: number }>(
    fetchApi,
    identityApi,
    baseUrl,
    '/api/v1/notifications/unread-count',
  );
  return data.count;
}

export async function markNotificationRead(
  fetchApi: FetchApi,
  identityApi: IdentityApi,
  baseUrl: string,
  id: string,
): Promise<Notification> {
  const data = await fetchNotificationApi<{ notification: Notification }>(
    fetchApi,
    identityApi,
    baseUrl,
    `/api/v1/notifications/${encodeURIComponent(id)}/read`,
    { method: 'PATCH' },
  );
  return data.notification;
}

export async function markAllNotificationsRead(
  fetchApi: FetchApi,
  identityApi: IdentityApi,
  baseUrl: string,
): Promise<number> {
  const data = await fetchNotificationApi<{ updated: number }>(
    fetchApi,
    identityApi,
    baseUrl,
    '/api/v1/notifications/read-all',
    { method: 'POST' },
  );
  return data.updated;
}

export async function clearAllNotifications(
  fetchApi: FetchApi,
  identityApi: IdentityApi,
  baseUrl: string,
): Promise<number> {
  const data = await fetchNotificationApi<{ deleted: number }>(
    fetchApi,
    identityApi,
    baseUrl,
    '/api/v1/notifications',
    { method: 'DELETE' },
  );
  return data.deleted;
}
